import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import AdminLayout from "../../commponents/layout/adminLayout";
import DosForm, { type DosFormValue } from "../../commponents/dos/DosForm";
import { editDos, getDosById } from "../../api/dosApi";
import { getApiErrorMessage, getHttpStatus } from "../../lib/httpError";
import { showInfo, showSuccess } from "../../utils/ alert";
import type { DosDetail, DosPayload } from "../../type/dos";

/**
 * CERMIN dari backend: src/dos/dosStatus.ts (DOS_EDITABLE_CODES).
 * Backend menolak edit status lain dengan 409.
 */
const DOS_EDITABLE_CODES = ["DRAFT", "REJECTED"];

const toFormValue = (dos: DosDetail): DosFormValue => ({
  customer: dos.customer
    ? {
        id: dos.customer.id,
        customer_name: dos.customer.customer_name,
        company_name: dos.customer.company_name,
        address: dos.customer.address,
        phone: dos.customer.phone,
      }
    : null,
  sales_id: dos.sales?.id ?? null,
  po_number: dos.po_number ?? "",
  // Detail mengirim YYYY-MM-DD, format yang dipakai input date.
  delivery_date: dos.delivery_date ?? "",
  due_date: dos.due_date ?? "",
  checked_by: dos.checked_by ?? "",
  received_by: dos.received_by ?? "",
  quotation: dos.quotation,
  items: (dos.items ?? []).map((item) => ({
    description: item.description,
    qty: Number(item.qty),
    unit: item.unit,
    remark: item.remark ?? "",
    product_id: item.product_id ?? null,
  })),
});

const loadErrorMessage = async (error: unknown) => {
  const status = getHttpStatus(error);
  if (status === 403) return "You don't have access to this DOS.";
  if (status === 404) return getApiErrorMessage(error, "DOS not found.");
  return getApiErrorMessage(error, "Failed to load DOS.");
};

export default function EditDos() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [dos, setDos] = useState<DosDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    const fetchDos = async () => {
      try {
        setLoading(true);
        setErrorMessage("");
        const response = await getDosById(Number(id));
        const data: DosDetail = response.data.data ?? response.data;

        if (!DOS_EDITABLE_CODES.includes(data.status?.code ?? "")) {
          showInfo(
            "Cannot edit DOS",
            `This DOS can no longer be edited (status: ${data.status?.name ?? "-"}).`
          );
          navigate(`/dos/${id}`, { replace: true });
          return;
        }

        setDos(data);
      } catch (error) {
        setErrorMessage(await loadErrorMessage(error));
      } finally {
        setLoading(false);
      }
    };

    if (id) fetchDos();
  }, [id]);

  // Status REJECTED tetap REJECTED setelah disimpan, sampai diajukan ulang.
  const handleSubmit = async (payload: DosPayload) => {
    await editDos(Number(id), payload);
    await showSuccess("DOS updated successfully.");
    navigate(`/dos/${id}`);
  };

  // 409: status sudah berubah (mis. sudah diajukan) -> kembali ke detail.
  const handleSubmitError = (error: unknown) => {
    if (getHttpStatus(error) === 409) navigate(`/dos/${id}`);
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="rounded-3xl bg-white p-6 text-center text-gray-400">Loading...</div>
      </AdminLayout>
    );
  }

  if (errorMessage || !dos) {
    return (
      <AdminLayout>
        <div className="rounded-3xl bg-white p-6 text-center">
          <p role="alert" className="mb-4 text-red-600">
            {errorMessage || "DOS not found."}
          </p>
          <button
            onClick={() => navigate("/dos")}
            className="rounded-xl border border-gray-200 px-5 py-2.5 font-medium text-gray-700 hover:bg-gray-50">
            Back to DOS List
          </button>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <DosForm
        title={`Edit DOS ${dos.dos_no}`}
        initialValue={toFormValue(dos)}
        rejectionReason={dos.status?.code === "REJECTED" ? dos.rejection_reason : null}
        onSubmit={handleSubmit}
        onSubmitError={handleSubmitError}
        onCancel={() => navigate(`/dos/${id}`)}
      />
    </AdminLayout>
  );
}
